/*
 * The fluid chart on the resident's screen.
 *
 * Adding a drink is one tap, because anything slower does not get done during a busy shift and
 * the chart ends up filled in at the end of the day from memory.
 *
 * The thickener sits at the top in its own right: it is not a medicine, but handing someone the
 * wrong consistency is a choking risk, so it is the first thing on the card.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import { Overlay } from './overlay.tsx';

type Plan = {
  targetMl: number | null;
  thickenerLevel: number | null;
  thickenerName: string;
  thickenerNote: string;
  reason: string;
};

type Entry = {
  id: string;
  kind: 'intake' | 'output';
  amountMl: number;
  what: string;
  note: string;
  at: string;
  recordedBy: string;
  cancels: string | null;
  cancelled: boolean;
  reason: string;
};

type Chart = {
  date: string;
  plan: Plan | null;
  intake: number;
  output: number;
  target: number | null;
  percent: number | null;
  short: number | null;
  measures: Array<{ ml: number; what: string }>;
  thickness: Array<{ level: number; name: string; help: string }>;
  entries: Entry[];
  week: Array<{ date: string; total: number }>;
};

export function FluidSection({ residentId, canPlan }: { residentId: string; canPlan: boolean }) {
  const [chart, setChart] = useState<Chart | null>(null);
  const [error, setError] = useState('');
  const [planning, setPlanning] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setChart(await api.get<Chart>(`/api/residents/${residentId}/fluids`));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the fluid chart.');
    }
  }, [residentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const add = useCallback(
    async (amountMl: number, what: string) => {
      setBusy(true);
      try {
        await api.post(`/api/residents/${residentId}/fluids`, { amountMl, what, kind: 'intake' });
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [residentId, load],
  );

  const correct = useCallback(
    async (entry: Entry) => {
      const reason = prompt(`Why is the ${entry.amountMl}ml ${entry.what.toLowerCase()} wrong?`);
      if (!reason || reason.trim().length < 3) return;
      const replacement = prompt('How much was it really? Leave empty if it should not be there at all.', '');
      try {
        await api.post(`/api/residents/${residentId}/fluids/${entry.id}/correct`, {
          reason,
          amountMl: replacement ? Number(replacement) : undefined,
        });
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      }
    },
    [residentId, load],
  );

  // Nothing set up and nothing recorded: this resident is not on a fluid chart.
  if (chart && !chart.plan && !chart.entries.length && !canPlan) return null;

  return (
    <>
      <h2>Drinks</h2>
      {error ? <p className="error" role="alert">{error}</p> : null}
      {!chart ? <p className="muted">Loading…</p> : null}

      {chart?.plan?.thickenerLevel ? (
        <p className="notice warn">
          <strong>Thickened drinks: {chart.plan.thickenerName} (level {chart.plan.thickenerLevel}).</strong>{' '}
          {chart.plan.thickenerNote || 'Every drink, including water and tea.'}
          {chart.plan.reason ? ` ${chart.plan.reason}` : ''}
        </p>
      ) : null}

      {chart ? (
        <div className="row static column dose">
          <div className="row-line">
            <span className="row-main">
              {chart.intake}ml today
              {chart.target ? <span className="muted"> of {chart.target}ml</span> : null}
            </span>
            {chart.percent !== null ? <span className="time">{chart.percent}%</span> : null}
          </div>

          {chart.target ? (
            <>
              <div className="bar" aria-label={`${chart.percent}% of the target`}>
                <span style={{ width: `${Math.min(100, chart.percent ?? 0)}%` }} />
              </div>
              {chart.short ? (
                <p className={`muted small ${chart.percent !== null && chart.percent < 50 ? 'warn-text' : ''}`}>
                  {chart.short}ml still to go today.
                </p>
              ) : (
                <p className="muted small">Target reached.</p>
              )}
            </>
          ) : (
            <p className="muted small">No daily target set.</p>
          )}

          <div className="row-actions">
            {chart.measures.map((measure) => (
              <button type="button" key={`${measure.ml}-${measure.what}`} className="quiet" disabled={busy}
                onClick={() => void add(measure.ml, measure.what)}>
                {measure.what} · {measure.ml}ml
              </button>
            ))}
            <button type="button" className="quiet" disabled={busy} onClick={() => {
              const answer = prompt('How much, in ml?');
              const amount = Number(answer);
              if (answer && Number.isFinite(amount) && amount > 0) {
                void add(amount, prompt('What was it?') || 'Drink');
              }
            }}>
              Something else
            </button>
            {canPlan ? (
              <button type="button" className="quiet" onClick={() => setPlanning(true)}>
                {chart.plan ? 'Change the plan' : 'Set a target'}
              </button>
            ) : null}
          </div>

          {chart.entries.length ? (
            <ul className="tray-items">
              {chart.entries
                .filter((entry) => !entry.cancels)
                .map((entry) => (
                  <li key={entry.id} className={entry.cancelled ? 'is-cancelled' : ''}>
                    <span className="time">{formatTime(entry.at)}</span> {entry.amountMl}ml {entry.what.toLowerCase()}
                    <span className="muted small"> · {entry.recordedBy}</span>
                    {entry.cancelled ? (
                      <span className="chip">corrected</span>
                    ) : (
                      <button type="button" className="quiet tiny" onClick={() => void correct(entry)}>Correct</button>
                    )}
                  </li>
                ))}
            </ul>
          ) : (
            <p className="muted small">Nothing recorded yet today.</p>
          )}

          {chart.week.length > 1 ? (
            <p className="muted small">
              Last few days: {chart.week.map((day) => `${shortDay(day.date)} ${day.total}ml`).join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}

      {planning && chart ? (
        <PlanDialog
          residentId={residentId}
          plan={chart.plan}
          thickness={chart.thickness}
          onClose={() => setPlanning(false)}
          onDone={() => {
            setPlanning(false);
            void load();
          }}
        />
      ) : null}
    </>
  );
}

function PlanDialog({
  residentId,
  plan,
  thickness,
  onClose,
  onDone,
}: {
  residentId: string;
  plan: Plan | null;
  thickness: Chart['thickness'];
  onClose: () => void;
  onDone: () => void;
}) {
  const [target, setTarget] = useState(plan?.targetMl ? String(plan.targetMl) : '1500');
  const [level, setLevel] = useState(plan?.thickenerLevel === null || plan?.thickenerLevel === undefined ? '' : String(plan.thickenerLevel));
  const [note, setNote] = useState(plan?.thickenerNote ?? '');
  const [reason, setReason] = useState(plan?.reason ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        await api.post(`/api/residents/${residentId}/fluid-plan`, {
          targetMl: target ? Number(target) : null,
          thickenerLevel: level === '' ? null : Number(level),
          thickenerNote: note,
          reason,
        });
        onDone();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [residentId, target, level, note, reason, onDone],
  );

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>Drinks plan</h2>

        <label htmlFor="fluid-target">How much should they have a day? (ml)</label>
        <input id="fluid-target" type="number" min="200" max="5000" step="50" value={target}
          onChange={(e) => setTarget(e.target.value)} />
        <p className="muted small">Most older adults are aimed at around 1500ml. Follow the care plan.</p>

        <label htmlFor="fluid-level">Thickness</label>
        <select id="fluid-level" value={level} onChange={(e) => setLevel(e.target.value)}>
          <option value="">Ordinary drinks, no thickener</option>
          {thickness.filter((t) => t.level > 0).map((t) => (
            <option key={t.level} value={t.level}>Level {t.level} — {t.name}: {t.help}</option>
          ))}
        </select>

        {level !== '' ? (
          <>
            <label htmlFor="fluid-note">How to make it up</label>
            <input id="fluid-note" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="One scoop per 200ml, stir and leave to stand" />
          </>
        ) : null}

        <label htmlFor="fluid-reason">Why, in words a carer will understand</label>
        <input id="fluid-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Swallowing assessed by speech and language therapy on 3 September" />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save the plan'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function formatTime(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getHours())}:${pad(when.getMinutes())}`;
}

function shortDay(date: string): string {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
}
